import * as React from "react";
import Board, { BoardProps } from "@cloudscape-design/board-components/board";
import BoardItem from "@cloudscape-design/board-components/board-item";
import { AuditTable } from "../HomePage/auditTable";
import { SkyscraperGraph } from "../HomePage/SkyscraperGraph";

interface ItemData {
  title: string;
}

type BoardItemType = BoardProps.Item<ItemData>;

const dragHandleStrings = {
  dragHandleAriaLabel: "Drag handle",
  dragHandleAriaDescription:
    "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to discard.",
  resizeHandleAriaLabel: "Resize handle",
  resizeHandleAriaDescription:
    "Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to discard.",
};

// The original skyscraper board, serving the public site while the car
// version stays behind the preview flag.
export const LegacyBoard = () => {
  const [items, setItems] = React.useState<BoardItemType[]>([
    { id: "graph", rowSpan: 5, columnSpan: 3, data: { title: "Skyscrapers by country" } },
    { id: "audit", rowSpan: 3, columnSpan: 2, data: { title: "Table of changes" } },
  ]);

  const renderContent = (id: string) => {
    switch (id) {
      case "graph":
        return <SkyscraperGraph />;
      case "audit":
        return <AuditTable />;
      default:
        return null;
    }
  };

  return (
    <Board<ItemData>
      renderItem={(boardItem) => (
        <BoardItem i18nStrings={dragHandleStrings}>
          {renderContent(boardItem.id)}
        </BoardItem>
      )}
      onItemsChange={(event) =>
        setItems([...event.detail.items] as BoardItemType[])
      }
      items={items}
      i18nStrings={(() => {
        function createAnnouncement(
          operationAnnouncement: string,
          conflicts: any[] | readonly BoardProps.Item<unknown>[],
          disturbed: string | any[] | readonly BoardProps.Item<unknown>[]
        ) {
          const conflictsAnnouncement =
            conflicts.length > 0
              ? `Conflicts with ${conflicts
                  .map((c) => (c.data as ItemData).title)
                  .join(", ")}.`
              : "";
          const disturbedAnnouncement =
            disturbed.length > 0 ? `Disturbed ${disturbed.length} items.` : "";
          return [
            operationAnnouncement,
            conflictsAnnouncement,
            disturbedAnnouncement,
          ]
            .filter(Boolean)
            .join(" ");
        }
        return {
          liveAnnouncementDndStarted: (operationType) =>
            operationType === "resize" ? "Resizing" : "Dragging",
          liveAnnouncementDndItemReordered: (operation) => {
            const columns = `column ${operation.placement.x + 1}`;
            const rows = `row ${operation.placement.y + 1}`;
            return createAnnouncement(
              `Item moved to ${
                operation.direction === "horizontal" ? columns : rows
              }.`,
              operation.conflicts,
              operation.disturbed
            );
          },
          liveAnnouncementDndItemResized: (operation) => {
            const columnsConstraint = operation.isMinimalColumnsReached
              ? " (minimal)"
              : "";
            const rowsConstraint = operation.isMinimalRowsReached
              ? " (minimal)"
              : "";
            const sizeAnnouncement =
              operation.direction === "horizontal"
                ? `columns ${operation.placement.width}${columnsConstraint}`
                : `rows ${operation.placement.height}${rowsConstraint}`;
            return createAnnouncement(
              `Item resized to ${sizeAnnouncement}.`,
              operation.conflicts,
              operation.disturbed
            );
          },
          liveAnnouncementDndItemInserted: (operation) => {
            const columns = `column ${operation.placement.x + 1}`;
            const rows = `row ${operation.placement.y + 1}`;
            return createAnnouncement(
              `Item inserted to ${columns}, ${rows}.`,
              operation.conflicts,
              operation.disturbed
            );
          },
          liveAnnouncementDndCommitted: (operationType) =>
            `${operationType} committed`,
          liveAnnouncementDndDiscarded: (operationType) =>
            `${operationType} discarded`,
          liveAnnouncementItemRemoved: (op) =>
            createAnnouncement(
              `Removed item: ${(op.item.data as ItemData).title}.`,
              [],
              op.disturbed
            ),
          navigationAriaLabel: "Board navigation",
          navigationAriaDescription:
            "Click on non-empty item to move focus over",
          navigationItemAriaLabel: (item) =>
            item ? (item.data as ItemData).title : "Empty",
        };
      })()}
      empty={undefined}
    />
  );
};
